import { studentFeeMonth } from '../utils/studentFeeMonths';


// client/src/components/StudentPaymentReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Axios baseURL (works with your dev server)
if (!axios.defaults.baseURL) {
  axios.defaults.baseURL = "http://localhost:5050";
}

// ---- Helpers ----

const TZ = "Asia/Kolkata";

function norm(s) {
  return String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
}
function matchesStudentQuery(student, q) {
  if (!q) return true;
  const n = norm(student?.name);
  const id = String(student?.studentId || "").toLowerCase();
  const phone = String(student?.phone || "");
  const whats = String(student?.whatsapp || "");
  return n.includes(q) || id.includes(q) || phone.includes(q) || whats.includes(q);
}

function toYMD(d) {
  if (!d) return "";
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = dtf.formatToParts(new Date(d));
  const y = parts.find((p) => p.type === "year")?.value || "";
  const m = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  return `${y}-${m}-${day}`;
}
function fmtINR(n) {
  return Number(n || 0).toLocaleString("en-IN");
}
function csvEscape(val) {
  const s = String(val ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function summarizeFeeLines(feeLines, payment) {
  if (!Array.isArray(feeLines)) return "-";
  const parts = [];
  let late = 0;
  for (const fl of feeLines) {
    const t = fl?.type;
    switch (t) {
      case "admissionFee":
        parts.push("Admission");
        break;
      case "monthlyFee":
        parts.push(`Monthly (${studentFeeMonth(payment) || 'month unavailable'})`);
        break;
      case "eventFee":
        parts.push(
          fl?.eventId?.name ? `Event (${fl.eventId.name})` : "Event"
        );
        break;
      case "oneTimeFee":
      case "onetimeFee":
      case "onetimeCourseFee":
        parts.push("One-time");
        break;
      case "miscFee":
        parts.push(fl?.note ? `Misc (${fl.note})` : "Misc");
        break;
      case "lateFee":
        late += Number(fl?.amount || 0);
        break;
      default:
        break;
    }
  }
  const uniq = [...new Set(parts)];
  if (late > 0) uniq.push(`+ Late ₹${fmtINR(late)}`);
  return uniq.join(" + ");
}
function money(n) {
  return `₹${fmtINR(n)}`;
}

// ---- PDF Export ----
function exportPDF({
  org = "All",
  studentLabel = "All Students",
  startDate,
  endDate,
  rows = [],
  totals,
  orgTotals = [], // array of [org, subtotal]
  modeSummary = {}, // { UPI: n, Cash: n, Card: n, NetBanking: n, Others: n }
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const padX = 40;

  // Org header line
  const orgLine =
    org === "COG" || org === "COG India Art Foundation"
      ? "COG India Art Foundation"
      : org === "Salankaraa"
      ? "Salankaraa School of Performing Arts"
      : `Organization: ${org}`;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Student Payment Report", padX, 40);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(orgLine, padX, 62);
  doc.text("P384 Keyatala Lane, Kolkata - 700029", padX, 80);

  const period = `Period: ${toYMD(startDate)} → ${toYMD(endDate)} | Student: ${studentLabel}`;
  doc.text(period, padX, 98);

  // Table
  const columns = [
    "Date",
    "Student (ID)",
    "Org",
    "Type",
    "Course",
    "Mode",
    "Note",
    "Amount (₹)",
  ];
  const body = rows.map((r) => [
    r.dateStr || "",
    r.studentLabel || "",
    r.org || "",
    r.type || "",
    r.course || "_",
    r.mode || "",
    r.note || "",
    fmtINR(r.total || 0),
  ]);

  autoTable(doc, {
    head: [columns],
    body,
    startY: 120,
    styles: { fontSize: 9, cellPadding: 6, halign: "left", valign: "middle" },
    headStyles: { fillColor: [67, 56, 202], textColor: 255 },
    columnStyles: { 7: { halign: "right" } },
    didDrawPage: () => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(9);
      doc.text(
        `Generated: ${new Intl.DateTimeFormat("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: TZ,
        }).format(new Date())}`,
        padX,
        pageHeight - 16
      );
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        pageWidth - padX,
        pageHeight - 16,
        { align: "right" }
      );
    },
  });

  // Org-wise subtotals table
  const afterMainY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 140;
  if (orgTotals && orgTotals.length) {
    autoTable(doc, {
      head: [["Organization", "Subtotal (₹)"]],
      body: orgTotals.map(([o, sum]) => [o, fmtINR(sum)]),
      startY: afterMainY,
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255 },
      columnStyles: { 1: { halign: "right" } },
    });
  }

  // Payment mode summary across filtered set
  const y2 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 16 : afterMainY + 16;
  const modes = ["UPI", "Cash", "Card", "NetBanking", "Others"];
  const modeRows = modes.map(m => [m, fmtINR(modeSummary?.[m] || 0)]);
  autoTable(doc, {
    head: [["Payment Mode", "Total (₹)"]],
    body: modeRows,
    startY: y2,
    styles: { fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 1: { halign: "right" } },
  });

  // Totals block (Summary)
  const y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 140;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Summary", padX, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const lines = [
    `Monthly: ${money(totals.monthly)}`,
    `Admission: ${money(totals.admission)}`,
    `Event: ${money(totals.event)}`,
    `One-time: ${money(totals.onetime)}`,
    `Misc: ${money(totals.misc)}`,
    `Grand Total: ${money(totals.grand)}`,
  ];
  lines.forEach((t, i) => doc.text(t, padX, y + 18 + i * 14));

  // Filename
  const safe = (s) => (s || "").replace(/[^a-z0-9-_]+/gi, "_");
  const file = `student_payments_${safe(org)}_${safe(
    toYMD(startDate)
  )}_${safe(toYMD(endDate))}.pdf`;
  doc.save(file);
}

// ---- Main Component ----
export default function StudentPaymentReport() {
  // Filters
  const today = toYMD(new Date());
  const startOfMonth = toYMD(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);

  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentQuery, setStudentQuery] = useState("");
  const [org, setOrg] = useState("All");
  const [type, setType] = useState("All");
  const [startDate, setStartDate] = useState(startOfMonth);
  const [endDate, setEndDate] = useState(today);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Load base data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setErr("");
        const [stuRes, payRes] = await Promise.all([
          axios.get("/api/students"),
          axios.get("/api/payments"),
        ]);
        if (cancelled) return;
        setStudents(Array.isArray(stuRes.data) ? stuRes.data : []);
        setPayments(Array.isArray(payRes.data) ? payRes.data : payRes.data?.payments || []);
      } catch (e) {
        console.error("StudentPaymentReport load failed", e);
        setErr("Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Student map + org list
  const studentMap = useMemo(() => {
    const m = new Map();
    for (const s of students) m.set(String(s._id), s);
    return m;
  }, [students]);

  const filteredStudents = useMemo(() => {
    const q = norm(studentQuery);
    if (!q) return students;
    return students.filter((s) => matchesStudentQuery(s, q));
  }, [students, studentQuery]);

  const orgOptions = useMemo(() => {
    const set = new Set(["All"]);
    students.forEach((s) => {
      const o = s?.course?.organization;
      if (o) set.add(o);
    });
    return Array.from(set);
  }, [students]);

  // Filtered rows
  const filtered = useMemo(() => {
    const sDate = new Date(startDate);
    const eDate = new Date(endDate);
    eDate.setHours(23, 59, 59, 999);

    return payments.filter((p) => {
      if (!p?.date) return false;
      const when = new Date(p.date);
      if (!(when >= sDate && when <= eDate)) return false;

      const sid = typeof p.studentId === "object" ? p.studentId?._id : p.studentId;
      const stu = studentMap.get(String(sid));

      if (selectedStudentId && String(sid) !== String(selectedStudentId)) return false;

      if (org !== "All") {
        const o = stu?.course?.organization;
        if (o !== org) return false;
      }

      if (type !== "All") {
        const hasType = (p?.feeLines || []).some((fl) => {
          if (type === "monthly" && fl.type === "monthlyFee") return true;
          if (type === "admission" && fl.type === "admissionFee") return true;
          if (type === "event" && fl.type === "eventFee") return true;
          if ((type === "onetime" || type === "oneTime") && ["onetimeFee", "oneTimeFee", "onetimeCourseFee"].includes(fl.type)) return true;
          if (type === "misc" && fl.type === "miscFee") return true;
          return false;
        });
        if (!hasType) return false;
      }

      return true;
    });
  }, [payments, startDate, endDate, selectedStudentId, org, type, studentMap]);

  // Rows for table
  const rows = useMemo(() => {
    return filtered.map((p) => {
      const sid = typeof p.studentId === "object" ? p.studentId?._id : p.studentId;
      const stu = studentMap.get(String(sid));
      const name =
        (typeof p.studentId === "object" ? p.studentId?.name : stu?.name) || "-";
      const code =
        (typeof p.studentId === "object" ? p.studentId?.studentId : stu?.studentId) || "";
      const studentLabel = code ? `${name} (${code})` : name;
      const courseName = stu?.course?.name || "_";
      const orgName = stu?.course?.organization || "";
      const teacherName = stu?.course?.teacher || "";
      const dateStr = toYMD(p?.date);
      const typeLabel = summarizeFeeLines(p?.feeLines, p);

      // Note: collect misc/event notes if present
      const notes = [];
      (p?.feeLines || []).forEach((fl) => {
        if (fl.type === "miscFee" && fl.note) {
          notes.push(`Misc: ${fl.note} (₹${fmtINR(fl.amount || 0)})`);
        }
        if (fl.type === "eventFee" && (fl?.eventId?.name || fl.note)) {
          const en = fl?.eventId?.name ? `Event: ${fl.eventId.name}` : "Event";
          const n = fl.note ? ` — ${fl.note}` : "";
          notes.push(`${en}${n} (₹${fmtINR(fl.amount || 0)})`);
        }
      });

      return {
        id: p._id,
        dateStr,
        studentLabel,
        course: courseName,
        org: orgName,
        teacher: teacherName,
        type: typeLabel,
        mode: p?.paymentMode || "",
        note: notes.join(" | "),
        total: Number(p?.totalAmount || 0),
      };
    });
  }, [filtered, studentMap]);
  // Org-wise subtotals and payment mode summary
  const orgTotals = useMemo(() => {
    const m = new Map();
    for (const p of filtered) {
      const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
      const stu = studentMap.get(String(sid));
      const o = stu?.course?.organization || 'Unknown';
      m.set(o, (m.get(o) || 0) + Number(p?.totalAmount || 0));
    }
    return Array.from(m.entries()).sort((a,b) => a[0].localeCompare(b[0]));
  }, [filtered, studentMap]);

  const modeSummary = useMemo(() => {
    const sum = { UPI: 0, Cash: 0, Card: 0, NetBanking: 0, Others: 0 };
    for (const p of filtered) {
      const k = (p?.paymentMode || 'Others');
      if (sum[k] == null) sum[k] = 0;
      sum[k] += Number(p?.totalAmount || 0);
    }
    return sum;
  }, [filtered]);

  // Totals
  const totals = useMemo(() => {
    const t = {
      monthly: 0,
      admission: 0,
      event: 0,
      onetime: 0,
      misc: 0,
      grand: 0,
    };
    for (const p of filtered) {
      for (const fl of p?.feeLines || []) {
        const amt = Number(fl?.amount || 0);
        switch (fl.type) {
          case "monthlyFee":
          case "lateFee":
            t.monthly += amt;
            break;
          case "admissionFee":
            t.admission += amt;
            break;
          case "eventFee":
            t.event += amt;
            break;
          case "oneTimeFee":
          case "onetimeFee":
          case "onetimeCourseFee":
            t.onetime += amt;
            break;
          case "miscFee":
            t.misc += amt;
            break;
          default:
            break;
        }
      }
      t.grand += Number(p?.totalAmount || 0);
    }
    return t;
  }, [filtered]);

  // Exports
  const handleExportCSV = () => {
    const head = [
      "Date",
      "Student",
      "Org",
      "Type",
      "Course",
      "Mode",
      "Note",
      "Amount",
    ];
    const data = rows.map((r) => [
      r.dateStr,
      r.studentLabel,
      r.org,
      r.type,
      r.course,
      r.mode,
      r.note,
      r.total,
    ]);
    const footer = [
      [],
      ["Summary"],
      ["Monthly", totals.monthly],
      ["Admission", totals.admission],
      ["Event", totals.event],
      ["One-time", totals.onetime],
      ["Misc", totals.misc],
      ["Grand Total", totals.grand],
    ];

    const csv =
      [head, ...data, [], ...footer]
        .map((row) => row.map(csvEscape).join(","))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const file = `student_payment_report_${toYMD(startDate)}_${toYMD(
      endDate
    )}.csv`;
    a.href = url;
    a.download = file;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const stu =
      selectedStudentId && studentMap.get(String(selectedStudentId));
    const studentLabel = stu
      ? `${stu.name} (${stu.studentId})`
      : "All Students";
    exportPDF({
      org,
      studentLabel,
      startDate,
      endDate,
      rows,
      totals,
      orgTotals,
      modeSummary,
    });
  };

  // UI
  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-4">Student Payment Report</h2>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Student</label>
            <input
              type="text"
              value={studentQuery}
              onChange={(e) => setStudentQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filteredStudents.length === 1) {
                  setSelectedStudentId(filteredStudents[0]._id);
                }
              }}
              placeholder="Search by name, code or phone…"
              className="border rounded px-3 py-2 w-full mb-2"
            />
            <select
              className="border rounded px-3 py-2 w-full"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              <option value="">All Students</option>
              {filteredStudents.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} {s.studentId ? `(${s.studentId})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Organization
            </label>
            <select
              className="border rounded px-3 py-2 w-full"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
            >
              {orgOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Payment Type
            </label>
            <select
              className="border rounded px-3 py-2 w-full"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option>All</option>
              <option value="admission">Admission</option>
              <option value="monthly">Monthly</option>
              <option value="event">Event</option>
              <option value="onetime">One-time</option>
              <option value="misc">Misc</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="border rounded px-3 py-2 w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">End Date</label>
            <input
              type="date"
              className="border rounded px-3 py-2 w-full"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
            onClick={handleExportCSV}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
            onClick={handleExportPDF}
          >
            Export PDF
          </button>
        </div>
      </div>

      {loading && <div>Loading…</div>}
      {err && <div className="text-red-600 mb-4">{err}</div>}

      {!loading && payments.some(payment=>!payment.date || !Number.isFinite(new Date(payment.date).getTime())) && <p className="text-sm text-amber-800 mb-3">Payments without a usable transaction date are excluded from date-filtered totals. Review their original records in Manage Payments; no date has been assigned automatically.</p>}
      {!loading && !err && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            <div className="bg-white rounded shadow p-3">
              <div className="text-xs text-gray-500">Monthly</div>
              <div className="font-semibold">{money(totals.monthly)}</div>
            </div>
            <div className="bg-white rounded shadow p-3">
              <div className="text-xs text-gray-500">Admission</div>
              <div className="font-semibold">{money(totals.admission)}</div>
            </div>
            <div className="bg-white rounded shadow p-3">
              <div className="text-xs text-gray-500">Event</div>
              <div className="font-semibold">{money(totals.event)}</div>
            </div>
            <div className="bg-white rounded shadow p-3">
              <div className="text-xs text-gray-500">One-time</div>
              <div className="font-semibold">{money(totals.onetime)}</div>
            </div>
            <div className="bg-white rounded shadow p-3">
              <div className="text-xs text-gray-500">Misc</div>
              <div className="font-semibold">{money(totals.misc)}</div>
            </div>
            <div className="bg-white rounded shadow p-3">
              <div className="text-xs text-gray-500">Grand Total</div>
              <div className="font-semibold">{money(totals.grand)}</div>
            </div>
          </div>

          {/* Org-wise Subtotals */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
            <div className="px-4 py-3 border-b font-medium">Org-wise Subtotals</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2">Organization</th>
                    <th className="text-right px-4 py-2">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {orgTotals.map(([o, sum]) => (
                    <tr key={o} className="border-t">
                      <td className="px-4 py-2">{o}</td>
                      <td className="px-4 py-2 text-right">₹{fmtINR(sum)}</td>
                    </tr>
                  ))}
                  {orgTotals.length === 0 && (
                    <tr><td className="px-4 py-4 text-center text-gray-500" colSpan={2}>No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Mode Summary */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
            <div className="px-4 py-3 border-b font-medium">Payment Method Summary</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2">Mode</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(modeSummary).map(([k,v]) => (
                    <tr key={k} className="border-t">
                      <td className="px-4 py-2">{k}</td>
                      <td className="px-4 py-2 text-right">₹{fmtINR(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b font-medium">Payments</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Student</th>
                    <th className="text-left px-4 py-2">Org</th>
                    <th className="text-left px-4 py-2">Teacher</th>
                    <th className="text-left px-4 py-2">Type</th>
                    <th className="text-left px-4 py-2">Course</th>
                    <th className="text-left px-4 py-2">Mode</th>
                    <th className="text-left px-4 py-2">Note</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-4 py-2">{r.dateStr}</td>
                      <td className="px-4 py-2">{r.studentLabel}</td>
                      <td className="px-4 py-2">{r.org}</td>
                      <td className="px-4 py-2">{r.teacher}</td>
                      <td className="px-4 py-2">{r.type}</td>
                      <td className="px-4 py-2">{r.course}</td>
                      <td className="px-4 py-2">{r.mode}</td>
                      <td className="px-4 py-2">{r.note}</td>
                      <td className="px-4 py-2 text-right">₹{fmtINR(r.total)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        className="px-4 py-6 text-center text-gray-500"
                        colSpan={9}
                      >
                        No payments for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 border-t">
                      <td className="px-4 py-2 font-medium" colSpan={8}>
                        Aggregate Total
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">
                        ₹{fmtINR(totals.grand)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}